<script>
  import ReceiptPageLayout from '../../components/receipt/ReceiptPageLayout.svelte';
  import ReceiptCard from '../../components/receipt/ReceiptCard.svelte';
  import ReceiptHeader from '../../components/receipt/ReceiptHeader.svelte';
  import ReceiptLabel from '../../components/receipt/ReceiptLabel.svelte';
  import ReceiptDivider from '../../components/receipt/ReceiptDivider.svelte';
  import ReceiptItemRow from '../../components/receipt/ReceiptItemRow.svelte';
  import ReceiptSummary from '../../components/receipt/ReceiptSummary.svelte';
  import ReceiptFooter from '../../components/receipt/ReceiptFooter.svelte';

  let {
    trip = {
      id: 'trip-001',
      name: '도쿄 벚꽃 여행',
      destination: 'TOKYO',
      startDate: '2026-03-20',
      endDate: '2026-03-26',
      budgetCents: 6000000,
      spentCents: 4500000,
    },
    categoryStats = [
      { key: 'food', label: '식비', amount: 1800000, count: 12 },
      { key: 'transport', label: '교통', amount: 800000, count: 8 },
      { key: 'accommodation', label: '숙소', amount: 1200000, count: 2 },
      { key: 'shopping', label: '쇼핑', amount: 450000, count: 5 },
      { key: 'activity', label: '액티비티', amount: 250000, count: 3 },
    ],
    exchangeRates = [
      { from: 'JPY', to: 'KRW', rate: 10.0 },
      { from: 'USD', to: 'KRW', rate: 1350.0 },
    ],
  } = $props();

  let remainingCents = $derived(trip.budgetCents - trip.spentCents);

  let totalDays = $derived(() => {
    const start = new Date(trip.startDate);
    const end = new Date(trip.endDate);
    return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
  });

  let dailyAvgCents = $derived(Math.round(trip.spentCents / totalDays()));

  let categoryTotal = $derived(categoryStats.reduce((sum, c) => sum + c.amount, 0));

  let categoryPercentages = $derived(
    categoryStats.map((c) => ({
      ...c,
      pct: categoryTotal > 0 ? Math.round((c.amount / categoryTotal) * 100) : 0,
    }))
  );

  let budgetUsagePct = $derived(
    trip.budgetCents > 0 ? Math.round((trip.spentCents / trip.budgetCents) * 100) : 0
  );

  let receiptNo = $derived(`FIN-${trip.id.replace(/\D/g, '').slice(-4).padStart(4, '0')}`);

  let timestamp = $derived(new Date().toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }));

  function fmt(cents) {
    return Math.round(cents / 100).toLocaleString();
  }

  function fmtRate(rate) {
    return rate % 1 === 0 ? rate.toFixed(1) : rate.toFixed(2);
  }
</script>

<ReceiptPageLayout>
  {#snippet header()}
    <ReceiptCard>
      <ReceiptHeader
        title={trip.destination}
        subtitle="FINANCE REPORT"
        receiptNo={receiptNo}
        date={`${trip.startDate} ~ ${trip.endDate}`}
      />
    </ReceiptCard>
  {/snippet}

  {#snippet content()}
    <ReceiptCard>
      <!-- Budget Overview -->
      <ReceiptLabel text="BUDGET OVERVIEW" />
      <ReceiptItemRow name="예산" amount={Math.round(trip.budgetCents / 100)} />
      <ReceiptItemRow name="지출" amount={Math.round(trip.spentCents / 100)} />
      <ReceiptItemRow
        name="잔액"
        amount={Math.round(Math.abs(remainingCents) / 100)}
        note={remainingCents < 0 ? '⚠ 예산 초과' : `${100 - budgetUsagePct}% 남음`}
      />

      <!-- Budget usage bar -->
      <div class="budget-bar">
        <div class="budget-bar__track">
          <div
            class="budget-bar__fill"
            class:budget-bar__fill--over={remainingCents < 0}
            style:width="{Math.min(budgetUsagePct, 100)}%"
          ></div>
        </div>
        <span class="budget-bar__label">{budgetUsagePct}% USED</span>
      </div>

      <ReceiptDivider variant="double" />

      <!-- By Category -->
      <ReceiptLabel text="BY CATEGORY" />
      {#each categoryPercentages as cat}
        <div class="category-row">
          <ReceiptItemRow
            name={cat.label}
            amount={Math.round(cat.amount / 100)}
            note={`${cat.count}건 · ${cat.pct}%`}
          />
          <div class="category-bar">
            <div class="category-bar__fill" style:width="{cat.pct}%"></div>
          </div>
        </div>
      {/each}

      <ReceiptDivider variant="dashed" />

      <!-- Exchange Rates -->
      <ReceiptLabel text="EXCHANGE RATES" />
      <div class="exchange-table">
        {#each exchangeRates as rate}
          <div class="exchange-row">
            <span class="exchange-from">1 {rate.from}</span>
            <span class="exchange-eq">=</span>
            <span class="exchange-to">₩{fmtRate(rate.rate)}</span>
          </div>
        {/each}
      </div>

      <ReceiptDivider variant="dashed" />

      <!-- Daily Average -->
      <ReceiptLabel text="DAILY AVERAGE" />
      <ReceiptItemRow name="일 평균 지출" amount={Math.round(dailyAvgCents / 100)} />
      <div class="plain-row">
        <span class="plain-row__name">총 여행일수</span>
        <span class="plain-row__dots"></span>
        <span class="plain-row__value">{totalDays()}일</span>
      </div>

      <ReceiptSummary
        items={[
          { label: '총 예산', amount: Math.round(trip.budgetCents / 100) },
          { label: '총 지출', amount: Math.round(trip.spentCents / 100) },
        ]}
        total={{ label: '잔액', amount: Math.round(Math.abs(remainingCents) / 100) }}
      />

      <ReceiptFooter
        lines={[
          `TRIP: ${trip.name}`,
          `PERIOD: ${trip.startDate} ~ ${trip.endDate}`,
          `CATEGORIES: ${categoryStats.length}`,
        ]}
        timestamp={timestamp}
      />
    </ReceiptCard>
  {/snippet}
</ReceiptPageLayout>

<style>
  .budget-bar {
    margin: 8px 0 4px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .budget-bar__track {
    flex: 1;
    height: 6px;
    background: var(--receipt-border, rgba(255, 255, 255, 0.08));
    border-radius: 3px;
    overflow: hidden;
  }

  .budget-bar__fill {
    height: 100%;
    background: var(--receipt-text-accent, #20B2AA);
    border-radius: 3px;
    transition: width 0.6s ease;
  }

  .budget-bar__fill--over {
    background: var(--receipt-text-excluded, #FF3B30);
  }

  .budget-bar__label {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--receipt-text-muted);
    letter-spacing: 1px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .category-row {
    margin-bottom: 2px;
  }

  .category-bar {
    height: 3px;
    background: var(--receipt-border, rgba(255, 255, 255, 0.08));
    border-radius: 1.5px;
    overflow: hidden;
    margin: -2px 0 6px;
  }

  .category-bar__fill {
    height: 100%;
    background: var(--receipt-text-accent, #20B2AA);
    opacity: 0.6;
    border-radius: 1.5px;
    transition: width 0.4s ease;
  }

  .exchange-table {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 4px;
  }

  .exchange-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-family: var(--font-mono);
    font-size: var(--receipt-size-body, 13px);
    color: var(--receipt-text-primary);
    line-height: 1.6;
  }

  .exchange-from {
    min-width: 60px;
    color: var(--receipt-text-secondary);
  }

  .exchange-eq {
    color: var(--receipt-text-muted);
  }

  .exchange-to {
    font-variant-numeric: tabular-nums;
    letter-spacing: var(--receipt-ls-amount, 0.5px);
  }

  .plain-row {
    display: flex;
    align-items: baseline;
    gap: 4px;
    width: 100%;
    font-family: var(--font-mono);
    font-size: var(--receipt-size-body, 13px);
    color: var(--receipt-text-primary);
    line-height: 1.6;
    margin-bottom: var(--receipt-item-gap, 6px);
  }

  .plain-row__name {
    flex-shrink: 0;
    max-width: 60%;
  }

  .plain-row__dots {
    flex: 1;
    min-width: 12px;
    border-bottom: 1px dotted var(--receipt-divider);
    margin-bottom: 4px;
  }

  .plain-row__value {
    font-variant-numeric: tabular-nums;
    letter-spacing: var(--receipt-ls-amount, 0.5px);
    white-space: nowrap;
    flex-shrink: 0;
  }
</style>
